import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { KeyRound, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Google OAuth credentials sub-section of Admin → Integrations tab.
 * Extracted from the 584-line monolith in iter123.
 */
export const GoogleOAuthSection = ({ googleOAuth, googleForm, setGoogleForm, saving, onSave }) => (
  <Card>
    <CardHeader>
      <CardTitle className="text-base flex items-center gap-2">
        <KeyRound className="w-5 h-5 text-blue-600" /> Google OAuth
      </CardTitle>
      <CardDescription>Used for &quot;Continue with Google&quot; social login &amp; Google Calendar integration.</CardDescription>
    </CardHeader>
    <CardContent className="space-y-4">
      {googleOAuth && googleOAuth.client_id_masked ? (
        <div className={`p-3 border rounded-lg text-sm ${
          googleOAuth.credential_source === 'admin_db'
            ? 'bg-emerald-50 border-emerald-200'
            : 'bg-blue-50 border-blue-200'
        }`}>
          <div className="flex items-center gap-2 font-medium">
            {googleOAuth.credential_source === 'admin_db' ? (
              <>
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                <span className="text-emerald-800">Using DB-saved credentials</span>
                <span className="text-xs px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-normal">admin panel</span>
              </>
            ) : (
              <>
                <AlertCircle className="w-4 h-4 text-blue-600" />
                <span className="text-blue-800">Using deployment env-var fallback</span>
                <span className="text-xs px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 font-normal">env</span>
              </>
            )}
          </div>
          <div className="mt-1 text-xs text-slate-600">
            Client ID: <span className="font-mono text-slate-800">{googleOAuth.client_id_masked}</span>
            {googleOAuth.credential_source === 'env' && (
              <span className="ml-2 italic">Saving credentials below will override this.</span>
            )}
          </div>
        </div>
      ) : (
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
          Not configured. Google Login and Calendar OAuth will not work until you save credentials below.
        </div>
      )}

      {googleOAuth?.redirect_uri && (
        <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg">
          <div className="text-xs text-slate-500 mb-1">
            Authorized redirect URI (add this to Google Cloud Console → OAuth 2.0 Client → Authorized redirect URIs):
          </div>
          <div className="flex items-center gap-2">
            <code className="flex-1 text-xs font-mono bg-white border border-slate-200 rounded px-2 py-1.5 text-slate-800 overflow-x-auto" data-testid="google-redirect-uri">
              {googleOAuth.redirect_uri}
            </code>
            <Button
              variant="outline" size="sm"
              onClick={() => {
                navigator.clipboard.writeText(googleOAuth.redirect_uri);
                toast.success('Redirect URI copied — paste it into Google Cloud Console');
              }}
              data-testid="google-copy-redirect-uri"
              className="shrink-0"
            >
              Copy
            </Button>
          </div>
          <div className="mt-1.5 text-xs text-slate-500">
            Google rejects the request with <span className="font-mono">Error 400 redirect_uri_mismatch</span> when this URL isn&apos;t whitelisted verbatim (protocol, host, and path all matter).
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label>Client ID</Label>
          <Input
            data-testid="google-client-id"
            value={googleForm.client_id}
            onChange={(e) => setGoogleForm({ ...googleForm, client_id: e.target.value })}
            placeholder="123456-abc.apps.googleusercontent.com"
          />
        </div>
        <div>
          <Label>Client Secret</Label>
          <Input
            data-testid="google-client-secret"
            type="password"
            value={googleForm.client_secret}
            onChange={(e) => setGoogleForm({ ...googleForm, client_secret: e.target.value })}
            placeholder="GOCSPX-..."
          />
        </div>
      </div>
      <Button onClick={onSave} disabled={saving} className="gap-1">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
        Save Google OAuth
      </Button>
    </CardContent>
  </Card>
);

export default GoogleOAuthSection;
